// Carrousel de bienvenue.
//
// Contrat avec le reste de la page :
//   - événement window « northvane:welcome:open »  → ouvre (NorthvaneWelcome.open()) ;
//   - événement window « northvane:welcome:close » → ferme ;
//   - événement window « northvane:welcome:closed » ← émis à la fermeture,
//     detail.reason ∈ skip | dismiss | free | finish (le tour guidé l'attend) ;
//   - window.__nvWelcomeOpen pendant l'affichage, window.__nvWelcomePending
//     tant qu'une ouverture automatique est prévue ;
//   - html.nv-welcome-lock pendant l'affichage.
//
// Le serveur apprend à l'ouverture que le carrousel a été vu (seenUrl), puis
// comment il a été quitté et sur quelle diapositive (closedUrl, User Stats) :
// la raison de fermeture, ou « plan » quand une offre est choisie (track).
//
// La coquille React ne se reconstruit pas : une fois ouvert dans ce document,
// le carrousel ne se rouvre plus tout seul, même si des props restaurées par
// l'historique disent encore autoOpen.
import { useCallback, useEffect, useRef, useState, type RefObject, type TouchEvent } from 'react'
import { flushSync } from 'react-dom'
import { router } from '@inertiajs/react'
import { csrfToken } from '@/lib/http'

export type CloseReason = 'skip' | 'dismiss' | 'free' | 'finish'
type Direction = 'forward' | 'back' | 'none'
export type SlideAnimation = '' | 'is-in-forward' | 'is-in-back'

type WelcomeWindow = Window & { __nvWelcomeOpen?: boolean; __nvWelcomePending?: boolean }
const win = () => window as WelcomeWindow

// Comptes pour lesquels le carrousel s'est déjà ouvert dans ce document. Écrit
// seulement dans le navigateur (jamais pendant le rendu serveur).
const openedInDocument = new Set<string>()

const prefersReducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
const lockPage = () => document.documentElement.classList.add('nv-welcome-lock')
const unlockPage = () => document.documentElement.classList.remove('nv-welcome-lock')

export type WelcomeCarousel = {
  autoOpen: boolean
  visible: boolean
  openClass: boolean
  // null tant que le carrousel n'a jamais été ouvert : état initial du
  // balisage (compteur vide, aucune diapositive masquée…).
  nav: { index: number; animation: SlideAnimation } | null
  dialogRef: RefObject<HTMLDivElement | null>
  close: (reason: CloseReason) => void
  track: (outcome: 'plan') => void
  next: () => void
  previous: () => void
  goTo: (index: number) => void
  onTouchStart: (event: TouchEvent) => void
  onTouchEnd: (event: TouchEvent) => void
}

export function useWelcomeCarousel(options: {
  autoOpen: boolean
  seenUrl: string
  closedUrl: string
  slides: readonly string[]
  total: number
  userKey: string
}): WelcomeCarousel {
  const { seenUrl, closedUrl, total, userKey } = options
  const autoOpen = options.autoOpen && !openedInDocument.has(userKey)

  const [visible, setVisible] = useState(false)
  const [openClass, setOpenClass] = useState(false)
  const [open, setOpen] = useState(false)
  const [nav, setNav] = useState<WelcomeCarousel['nav']>(null)

  const dialogRef = useRef<HTMLDivElement | null>(null)
  const isOpenRef = useRef(false)
  const indexRef = useRef(0)
  const totalRef = useRef(total)
  totalRef.current = total
  const slidesRef = useRef(options.slides)
  slidesRef.current = options.slides
  const reportedRef = useRef(false)
  const returnFocusRef = useRef<Element | null>(null)
  const closeTimerRef = useRef<number | undefined>(undefined)
  const rafRef = useRef<number | undefined>(undefined)
  const swipeRef = useRef<{ x: number; y: number } | null>(null)

  const go = useCallback((index: number, direction: Direction) => {
    const i = Math.max(0, Math.min(index, totalRef.current - 1))
    indexRef.current = i
    const animation: SlideAnimation =
      direction !== 'none' && !prefersReducedMotion() ? (direction === 'back' ? 'is-in-back' : 'is-in-forward') : ''
    setNav({ index: i, animation })
  }, [])

  const markSeen = useCallback(() => {
    if (!seenUrl) return
    fetch(seenUrl, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
    }).catch(() => { /* le suivi « vu » ne doit jamais casser le carrousel */ })
  }, [seenUrl])

  // Une sortie par ouverture : un choix d'offre (« plan ») passe avant la
  // fermeture que déclenche ensuite la navigation.
  const report = useCallback((outcome: CloseReason | 'plan') => {
    if (!closedUrl || reportedRef.current) return
    reportedRef.current = true
    fetch(closedUrl, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ outcome, slide: slidesRef.current[indexRef.current] }),
    }).catch(() => { /* le suivi ne doit jamais casser le carrousel */ })
  }, [closedUrl])

  const openCarousel = useCallback(() => {
    if (isOpenRef.current) return
    isOpenRef.current = true
    reportedRef.current = false
    win().__nvWelcomeOpen = true
    win().__nvWelcomePending = false
    openedInDocument.add(userKey)
    returnFocusRef.current = document.activeElement
    // Une fermeture encore en cours ne doit pas masquer la réouverture.
    window.clearTimeout(closeTimerRef.current)

    setVisible(true)
    setOpen(true)
    lockPage()
    go(0, 'none')
    markSeen()
  }, [go, markSeen, userKey])

  // Rendu sans [hidden] : la classe d'ouverture à l'image suivante, pour que
  // les transitions partent de l'état fermé.
  useEffect(() => {
    if (!open) return
    rafRef.current = requestAnimationFrame(() => {
      flushSync(() => setOpenClass(true))
      dialogRef.current?.focus({ preventScroll: true })
    })
    return () => cancelAnimationFrame(rafRef.current!)
  }, [open])

  const stripDebugParam = useCallback(() => {
    // ?welcome=1 est un interrupteur de débogage : le laisser dans l'URL
    // rejouerait le carrousel à chaque rechargement. Visite locale d'Inertia :
    // l'adresse de la page et son entrée d'historique (défilement compris)
    // suivent, sans requête ni événement navigate.
    const url = new URL(window.location.href)
    if (!url.searchParams.has('welcome')) return
    url.searchParams.delete('welcome')
    const target = url.pathname + url.search + url.hash
    try {
      router.replace({ url: target, preserveScroll: true, preserveState: true })
    } catch {
      history.replaceState(history.state, '', target)
    }
  }, [])

  const close = useCallback((reason: CloseReason) => {
    if (!isOpenRef.current) return
    report(reason)
    isOpenRef.current = false
    win().__nvWelcomeOpen = false
    cancelAnimationFrame(rafRef.current!)
    setOpen(false)
    setOpenClass(false)

    const finish = () => {
      setVisible(false)
      unlockPage()
      stripDebugParam()
      const back = returnFocusRef.current as HTMLElement | null
      if (back && typeof back.focus === 'function') back.focus({ preventScroll: true })
      window.dispatchEvent(new CustomEvent('northvane:welcome:closed', { detail: { reason } }))
    }
    // Attendre le fondu, sans dépendre de transitionend.
    if (prefersReducedMotion()) finish()
    else closeTimerRef.current = window.setTimeout(finish, 220)
  }, [report, stripDebugParam])

  const next = useCallback(() => {
    if (indexRef.current >= totalRef.current - 1) {
      close('finish')
      return
    }
    go(indexRef.current + 1, 'forward')
  }, [close, go])

  const previous = useCallback(() => {
    if (indexRef.current > 0) go(indexRef.current - 1, 'back')
  }, [go])

  const goTo = useCallback((index: number) => {
    if (Number.isNaN(index) || index === indexRef.current) return
    go(index, index > indexRef.current ? 'forward' : 'back')
  }, [go])

  // Clavier : Échap, flèches, piège à focus.
  useEffect(() => {
    if (!open) return
    const trapFocus = (event: KeyboardEvent) => {
      const dialog = dialogRef.current
      if (!dialog) return
      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([type=hidden]), [tabindex]:not([tabindex="-1"])',
      )).filter((el) => !el.closest('[hidden]') && !el.closest('[inert]') && el.offsetParent !== null)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    const onKeydown = (event: KeyboardEvent) => {
      if (!isOpenRef.current) return
      const typing = (event.target as Element | null)?.closest?.('input, textarea, select')
      if (event.key === 'Escape') {
        event.preventDefault()
        close('dismiss')
      } else if (event.key === 'ArrowRight' && !typing) {
        if (indexRef.current < totalRef.current - 1) next()
      } else if (event.key === 'ArrowLeft' && !typing) {
        previous()
      } else if (event.key === 'Tab') {
        trapFocus(event)
      }
    }
    document.addEventListener('keydown', onKeydown)
    return () => document.removeEventListener('keydown', onKeydown)
  }, [open, close, next, previous])

  // NorthvaneWelcome.open() / close().
  useEffect(() => {
    const onOpen = () => openCarousel()
    const onClose = () => close('dismiss')
    window.addEventListener('northvane:welcome:open', onOpen)
    window.addEventListener('northvane:welcome:close', onClose)
    return () => {
      window.removeEventListener('northvane:welcome:open', onOpen)
      window.removeEventListener('northvane:welcome:close', onClose)
    }
  }, [openCarousel, close])

  // Ouverture automatique, une fois le chargeur initial parti (s'ouvrir
  // dessous gâcherait l'entrée du carrousel).
  useEffect(() => {
    if (!autoOpen) return
    win().__nvWelcomePending = true
    let openTimer: number | undefined
    let readyPoll: number | undefined
    const ready = () => {
      const loader = document.getElementById('initial-loader')
      return !loader || loader.classList.contains('hidden') || document.documentElement.classList.contains('loader-already-shown')
    }
    if (ready()) {
      openTimer = window.setTimeout(openCarousel, 400)
    } else {
      const startedAt = Date.now()
      readyPoll = window.setInterval(() => {
        if (ready() || Date.now() - startedAt > 8000) {
          window.clearInterval(readyPoll)
          openTimer = window.setTimeout(openCarousel, 400)
        }
      }, 150)
    }
    return () => {
      window.clearInterval(readyPoll)
      window.clearTimeout(openTimer)
    }
  }, [autoOpen, openCarousel])

  // Navigation pendant l'affichage (bouton Retour) : la page change dessous ;
  // on le retire sans événement « closed ».
  useEffect(
    () =>
      router.on('navigate', () => {
        if (!isOpenRef.current) return
        isOpenRef.current = false
        cancelAnimationFrame(rafRef.current!)
        window.clearTimeout(closeTimerRef.current)
        unlockPage()
        win().__nvWelcomeOpen = false
        win().__nvWelcomePending = false
        setOpen(false)
        setOpenClass(false)
        setVisible(false)
        setNav(null)
        indexRef.current = 0
      }),
    [],
  )

  // Démontage (props sans carrousel) : plus rien n'attend le carrousel.
  useEffect(
    () => () => {
      window.clearTimeout(closeTimerRef.current)
      cancelAnimationFrame(rafRef.current!)
      if (isOpenRef.current) unlockPage()
      isOpenRef.current = false
      win().__nvWelcomeOpen = false
      win().__nvWelcomePending = false
    },
    [],
  )

  // Balayage horizontal et franc seulement : un défilement vertical ne doit
  // jamais changer de diapositive.
  const onTouchStart = useCallback((event: TouchEvent) => {
    swipeRef.current = { x: event.touches[0].clientX, y: event.touches[0].clientY }
  }, [])
  const onTouchEnd = useCallback((event: TouchEvent) => {
    const start = swipeRef.current
    if (!start) return
    swipeRef.current = null
    const dx = event.changedTouches[0].clientX - start.x
    const dy = event.changedTouches[0].clientY - start.y
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return
    if (dx < 0 && indexRef.current < totalRef.current - 1) next()
    if (dx > 0) previous()
  }, [next, previous])

  return { autoOpen, visible, openClass, nav, dialogRef, close, track: report, next, previous, goTo, onTouchStart, onTouchEnd }
}
